/**
 * Admin Messages Inbox — staff/admin/owner view of every contact thread.
 *
 * Left pane = thread list (filterable by status).
 * Right pane = selected thread + reply composer.
 *
 * Polls the unread count every 30s so the badge in the side nav stays
 * fresh without a websocket.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, X, Inbox, CheckCircle2, RotateCcw } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import PageTitle from '../components/PageTitle';
import SpellCheckDialog from '../components/SpellCheckDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusPill = (status) => {
  if (status === 'closed') {
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#F1F5F9] text-[#64748B]">Closed</span>;
  }
  return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#ECFDF5] text-[#047857]">Open</span>;
};

const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-GB');
  } catch { return ''; }
};

const AdminMessages = () => {
  const [threads, setThreads] = useState([]);
  const [filter, setFilter] = useState('open'); // 'open' | 'closed' | 'all'
  const [activeId, setActiveId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadThreads = useCallback(async () => {
    try {
      const url = filter === 'all'
        ? `${API}/admin/messages/threads`
        : `${API}/admin/messages/threads?status=${filter}`;
      const r = await axios.get(url, { withCredentials: true });
      setThreads(r.data?.threads || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load threads.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = async (tid) => {
    setActiveId(tid);
    setReply('');
    setActiveThread(null);
    try {
      const r = await axios.get(`${API}/admin/messages/threads/${tid}`, { withCredentials: true });
      setActiveThread(r.data);
      // Refresh the list to clear unread badge on the row we just opened.
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load thread.');
    }
  };

  // Spell-check state for the reply composer — staff replies hit the
  // customer's inbox so we want US→UK swaps flagged before send.
  const [pendingReply, setPendingReply] = useState(null);

  const sendReplyText = async (text) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/messages/threads/${activeId}/reply`,
        { body: text.trim() },
        { withCredentials: true },
      );
      toast.success('Reply sent.');
      setReply('');
      openThread(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reply failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeId) return;
    try {
      const r = await axios.post(
        `${API}/admin/spell-check`,
        { payload: reply.trim() },
        { withCredentials: true },
      );
      if (r.data?.total_issues > 0) {
        setPendingReply({ text: reply.trim(), findings: r.data.findings });
        return;
      }
    } catch (err) {
      console.warn('Spell-check failed, sending without it:', err);
    }
    await sendReplyText(reply);
  };

  const applyFixesAndSend = async () => {
    if (!pendingReply) return;
    try {
      const r = await axios.post(
        `${API}/admin/spell-check/apply`,
        { payload: pendingReply.text },
        { withCredentials: true },
      );
      setPendingReply(null);
      await sendReplyText(r.data?.payload || pendingReply.text);
    } catch (err) {
      console.warn('Spell-check apply failed:', err);
    }
  };

  const sendAsIs = async () => {
    if (!pendingReply) return;
    const text = pendingReply.text;
    setPendingReply(null);
    await sendReplyText(text);
  };

  const closeThread = async () => {
    if (!activeId) return;
    try {
      await axios.post(`${API}/admin/messages/threads/${activeId}/close`, {}, { withCredentials: true });
      toast.success('Thread closed.');
      openThread(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Close failed.');
    }
  };

  const reopenThread = async () => {
    if (!activeId) return;
    try {
      await axios.post(`${API}/admin/messages/threads/${activeId}/reopen`, {}, { withCredentials: true });
      toast.success('Thread reopened.');
      openThread(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reopen failed.');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-messages-page">
      <PageTitle title="Inbox" subtitle="Customer messages — every one read by a person." className="mb-6" />

      <div className="flex gap-2 mb-4" data-testid="admin-messages-filters">
        {['open', 'closed', 'all'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${filter === s ? 'bg-[#020617] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'}`}
            data-testid={`admin-messages-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-4 min-h-[600px]">
        {/* Thread list */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid="admin-messages-thread-list">
          {loading ? (
            <div className="p-6 text-sm text-[#64748B]">Loading…</div>
          ) : threads.length === 0 ? (
            <div className="p-6 text-center">
              <Inbox className="w-8 h-8 mx-auto text-[#94A3B8] mb-2" />
              <p className="text-sm text-[#64748B]">No threads in this view.</p>
            </div>
          ) : (
            <ul>
              {threads.map((t) => (
                <li key={t.thread_id}>
                  <button
                    type="button"
                    onClick={() => openThread(t.thread_id)}
                    className={`w-full text-left px-4 py-3 border-b border-[#E2E8F0] transition-colors ${activeId === t.thread_id ? 'bg-[#EFF6FF]' : 'hover:bg-[#F8FAFC]'}`}
                    data-testid={`admin-messages-thread-row-${t.thread_id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-semibold text-[#020617] text-sm truncate flex-1">
                        {t.has_unread_for_admin && (
                          <span className="inline-block w-2 h-2 bg-[#0052FF] rounded-full mr-1.5" data-testid={`admin-messages-unread-dot-${t.thread_id}`}></span>
                        )}
                        {t.subject}
                      </div>
                      {statusPill(t.status)}
                    </div>
                    <div className="text-xs text-[#64748B] mb-1">{t.owner_label} · {fmtTime(t.last_message_at)}</div>
                    <div className="text-xs text-[#94A3B8] truncate">{t.last_message_preview}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Thread detail */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl flex flex-col" data-testid="admin-messages-thread-detail">
          {!activeThread ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#64748B]">
              {threads.length === 0 ? 'Nothing to read yet.' : 'Select a thread on the left.'}
            </div>
          ) : (
            <>
              <div className="p-5 border-b border-[#E2E8F0] flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-extrabold text-[#020617]" data-testid="admin-messages-active-subject">{activeThread.subject}</h2>
                  <div className="text-xs text-[#64748B] mt-1">
                    From <span className="font-semibold text-[#020617]" data-testid="admin-messages-active-owner">{activeThread.owner_label}</span>
                    {activeThread.owner_email && <> &middot; <a href={`mailto:${activeThread.owner_email}`} className="text-[#0052FF] hover:underline">{activeThread.owner_email}</a></>}
                    {' · '}{statusPill(activeThread.status)}
                  </div>
                </div>
                {activeThread.status === 'open' ? (
                  <Button
                    type="button"
                    onClick={closeThread}
                    className="bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]"
                    data-testid="admin-messages-close-button"
                  >
                    <X className="w-4 h-4 mr-1" /> Close
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={reopenThread}
                    className="bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]"
                    data-testid="admin-messages-reopen-button"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Reopen
                  </Button>
                )}
              </div>

              <div className="flex-1 p-5 space-y-4 overflow-y-auto max-h-[55vh]" data-testid="admin-messages-message-list">
                {activeThread.messages.map((m) => {
                  const isAdmin = m.sender_type === 'admin';
                  return (
                    <div
                      key={m.message_id}
                      className={`max-w-[80%] ${isAdmin ? 'ml-auto' : ''}`}
                      data-testid={`admin-messages-message-${m.message_id}`}
                    >
                      <div className={`text-[11px] font-semibold uppercase tracking-wider mb-1 ${isAdmin ? 'text-[#0052FF] text-right' : 'text-[#64748B]'}`}>
                        {isAdmin ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : null}
                        {m.sender_label} · {fmtTime(m.created_at)}
                      </div>
                      <div
                        className={`p-3 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${isAdmin ? 'bg-[#0052FF] text-white' : 'bg-[#F1F5F9] text-[#020617]'}`}
                      >
                        {m.body}
                      </div>
                    </div>
                  );
                })}
              </div>

              {activeThread.status === 'open' && (
                <div className="p-4 border-t border-[#E2E8F0]">
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Type your reply…"
                    rows={4}
                    maxLength={5000}
                    className="mb-3"
                    data-testid="admin-messages-reply-input"
                  />
                  <Button
                    type="button"
                    onClick={sendReply}
                    disabled={submitting || !reply.trim()}
                    className="bg-[#0052FF] text-white hover:bg-[#003ECC]"
                    data-testid="admin-messages-reply-submit"
                  >
                    <Send className="w-4 h-4 mr-1.5" />
                    {submitting ? 'Sending…' : 'Reply'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <p className="mt-4 text-xs text-[#94A3B8] flex items-center gap-1.5">
        <Mail className="w-3.5 h-3.5" />
        Replies appear in the customer&apos;s Account Settings → Messages tab. Your business email stays hidden.
      </p>
      <SpellCheckDialog
        open={!!pendingReply}
        onOpenChange={(open) => { if (!open) setPendingReply(null); }}
        findings={pendingReply?.findings || []}
        onApplyAll={applyFixesAndSend}
        onSaveAsIs={sendAsIs}
        busy={submitting}
      />
    </div>
  );
};

export default AdminMessages;
